'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { signInWithSso } from '@/app/actions/sso';
import {
  SSO_NOT_FOUND_MESSAGE,
  SSO_REDIRECT_NOTICE,
  SSO_START_FAILED_MESSAGE,
} from '@/utils/auth/sso-copy';
import { getSafeRedirectPath } from '@/utils/redirect-validation';

interface LoginSsoStepProps {
  /** Pre-filled from `?sso=`, which is what an identity provider's app tile links to. */
  initialSlug?: string;
  /** Set only when the user has already been refused and is being sent where they belong. */
  autoStart?: boolean;
  /** Carried from the sign-in form so the provider can skip asking for the address again. */
  email?: string;
  onBack: () => void;
}

export function LoginSsoStep({
  initialSlug = '',
  autoStart = false,
  email,
  onBack,
}: LoginSsoStepProps) {
  const searchParams = useSearchParams();
  const [company, setCompany] = useState(initialSlug);
  const [loading, setLoading] = useState(autoStart && Boolean(initialSlug));
  const [error, setError] = useState<string | null>(null);
  const hasAutoStarted = useRef(false);

  // Answers with what to tell the user, and sets no state itself, so the effect below changes
  // nothing synchronously.
  const start = useCallback(
    async (slug: string, automatic = false): Promise<string | null> => {
      const { url, error: startError } = await signInWithSso({
        slug,
        email,
        redirectTo: getSafeRedirectPath(searchParams.get('returnTo')),
        automatic,
      });

      if (url) {
        window.location.href = url;
        return null;
      }

      return startError === 'sso_not_found' ? SSO_NOT_FOUND_MESSAGE : SSO_START_FAILED_MESSAGE;
    },
    [email, searchParams]
  );

  useEffect(() => {
    // A bare ?sso= link fills the field and waits for a click; only a refusal we already issued
    // starts by itself. The destination is the company's configured provider either way, so a
    // crafted link can at worst bounce someone to their own employer's sign-in page.
    if (!autoStart || !initialSlug || hasAutoStarted.current) return;
    hasAutoStarted.current = true;

    void start(initialSlug.trim().toLowerCase(), true).then(message => {
      if (!message) return;
      setError(message);
      setLoading(false);
    });
  }, [autoStart, initialSlug, start]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const slug = company.trim().toLowerCase();
    if (!slug) return;

    setLoading(true);
    setError(null);

    void start(slug).then(message => {
      if (!message) return;
      setError(message);
      setLoading(false);
    });
  };

  return (
    <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-2">
      <div className="space-y-2">
        <Label htmlFor="sso-company">Company</Label>
        <Input
          className="mt-1.5"
          type="text"
          id="sso-company"
          name="company"
          value={company}
          onChange={event => setCompany(event.target.value)}
          // A company arriving from an app tile is already filled in, so focus belongs on Continue.
          autoFocus={!initialSlug}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
        />
      </div>

      {error && <p className="text-center text-sm text-red-500">{error}</p>}
      {loading && !error && (
        <p className="text-center text-sm text-blue-25">{SSO_REDIRECT_NOTICE}</p>
      )}

      <Button
        type="submit"
        isLoading={loading}
        loadingText="Redirecting..."
        className="mt-2 w-full bg-black"
        autoFocus={Boolean(initialSlug) && !autoStart}
      >
        Continue
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="mt-4 text-sm text-blue-500 hover:text-blue-700"
      >
        Back to sign in
      </button>
    </form>
  );
}
